/**-----------------------------------------------------------------------------------------
* Copyright © 2026 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import * as i0 from '@angular/core';
import { Input, HostBinding, Directive, Component, isDevMode, ChangeDetectionStrategy, Injectable, InjectionToken, Optional, Inject, NgModule } from '@angular/core';
import { isDocumentAvailable, areObjectsEqual } from '@progress/kendo-angular-common';
import { validatePackage } from '@progress/kendo-licensing';
import * as i1_1 from '@angular/platform-browser';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { NgClass } from '@angular/common';

/**
 * @hidden
 */
function SVGIconComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵnamespaceSVG();
    i0.ɵɵdomElement(0, "svg", 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("pointer-events", "none");
    i0.ɵɵdomProperty("innerHTML", ctx_r0.content, i0.ɵɵsanitizeHtml);
    i0.ɵɵattribute("viewBox", ctx_r0.icon.viewBox);
  }
}
function IconWrapperComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-svgicon", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.innerCssClass)("icon", ctx_r0.svgIcon)("size", ctx_r0.size || (ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.size))("themeColor", ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.themeColor)("flip", ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.flip);
  }
}
function IconWrapperComponent_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon", 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngClass", ctx_r0.innerCssClass)("name", ctx_r0.name)("size", ctx_r0.size || (ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.size))("themeColor", ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.themeColor)("flip", ctx_r0.iconSettings == null ? null : ctx_r0.iconSettings.flip);
  }
}
function IconWrapperComponent_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngClass", ctx_r0.customClasses);
  }
}
function IconWrapperComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, IconWrapperComponent_Conditional_1_Conditional_0_Template, 1, 5, "kendo-icon", 1)(1, IconWrapperComponent_Conditional_1_Conditional_1_Template, 1, 1, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(!ctx_r0.customFontIconClass ? 0 : 1);
  }
}
const packageMetadata = {
  name: '@progress/kendo-angular-icons',
  productName: 'Kendo UI for Angular',
  productCode: 'KENDOUIANGULAR',
  productCodes: ['KENDOUIANGULAR'],
  publishDate: 1780593623,
  version: '24.1.0',
  licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/'
};
const sizeClasses = {
  'default': 'k-icon',
  'xsmall': 'k-icon-xs',
  'small': 'k-icon-sm',
  'medium': 'k-icon-md',
  'large': 'k-icon-lg',
  'xlarge': 'k-icon-xl',
  'xxlarge': 'k-icon-xxl',
  'xxxlarge': 'k-icon-xxxl'
};
/**
 * @hidden
 */
class IconBaseDirective {
  element;
  renderer;
  get horizontalFlip() {
    return this.flip === 'horizontal' || this.flip === 'both';
  }
  get verticalFlip() {
    return this.flip === 'vertical' || this.flip === 'both';
  }
  /**
   * Flips the icon horizontally, vertically, or in both directions.
   */
  flip;
  /**
   * Sets the `IconThemeColor` for the icon. Use this property to apply a theme color.
   */
  set themeColor(themeColor) {
    const element = this.element.nativeElement;
    if (this._themeColor) {
      this.renderer.removeClass(element, `!k-color-${this._themeColor}`);
    }
    this._themeColor = themeColor;
    // needed to ensure the theme color will be always applied; e.g. the class .k-button-icon, specific
    // for icon buttons applies "color: inherit" which overwrites the theme color
    if (themeColor) {
      this.renderer.addClass(element, `!k-color-${themeColor}`);
    }
  }
  get themeColor() {
    return this._themeColor;
  }
  /**
   * Sets the `IconSize` for the icon. Use this property to change the icon size.
   */
  set size(size) {
    const currentClass = sizeClasses[this.size];
    const newClass = sizeClasses[size];
    const element = this.element.nativeElement;
    this.renderer.removeClass(element, currentClass);
    if (size && size !== 'default') {
      this.renderer.addClass(element, newClass);
    }
    this._size = size;
  }
  get size() {
    return this._size;
  }
  _themeColor;
  _size;
  constructor(element, renderer) {
    this.element = element;
    this.renderer = renderer;
    validatePackage(packageMetadata);
  }
  static ɵfac = function IconBaseDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconBaseDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: IconBaseDirective,
    selectors: [["", "kendoIconBase", ""]],
    hostVars: 4,
    hostBindings: function IconBaseDirective_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-flip-h", ctx.horizontalFlip)("k-flip-v", ctx.verticalFlip);
      }
    },
    inputs: {
      flip: "flip",
      themeColor: "themeColor",
      size: "size"
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconBaseDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoIconBase]'
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }], {
    horizontalFlip: [{
      type: HostBinding,
      args: ['class.k-flip-h']
    }],
    verticalFlip: [{
      type: HostBinding,
      args: ['class.k-flip-v']
    }],
    flip: [{
      type: Input
    }],
    themeColor: [{
      type: Input
    }],
    size: [{
      type: Input
    }]
  });
})();

/**
 * Represents the [Kendo UI Icon component for Angular](https://www.telerik.com/kendo-angular-ui/components/icons/icon).
 * Used to display icons from the [Kendo UI Icons](https://www.telerik.com/kendo-angular-ui/components/icons/icon/icon-list) collection.
 *
 * @example
 * ```html
 * <kendo-icon name="home"></kendo-icon>
 * ```
 */
class IconComponent extends IconBaseDirective {
  element;
  renderer;
  hostClass = true;
  hostAriaHidden = true;
  /**
   * Sets the `Icon` to render. Supports all [Kendo UI Icons](https://www.telerik.com/kendo-angular-ui/components/icons/icon/icon-list).
   */
  set name(name) {
    if (isDocumentAvailable()) {
      const newName = name !== this._name;
      if (newName) {
        const element = this.element.nativeElement;
        this.renderer.removeClass(element, `k-i-${this.name}`);
        this.renderer.addClass(element, `k-i-${name}`);
      }
    }
    this._name = name;
  }
  get name() {
    return this._name;
  }
  _name;
  constructor(element, renderer) {
    super(element, renderer);
    this.element = element;
    this.renderer = renderer;
  }
  static ɵfac = function IconComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: IconComponent,
    selectors: [["kendo-icon"]],
    hostVars: 5,
    hostBindings: function IconComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("aria-hidden", ctx.hostAriaHidden);
        i0.ɵɵclassProp("k-icon", ctx.hostClass)("k-font-icon", ctx.hostClass);
      }
    },
    inputs: {
      name: "name"
    },
    exportAs: ["kendoIcon"],
    features: [i0.ɵɵInheritDefinitionFeature],
    decls: 0,
    vars: 0,
    template: function IconComponent_Template(rf, ctx) {},
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconComponent, [{
    type: Component,
    args: [{
      exportAs: 'kendoIcon',
      selector: 'kendo-icon',
      template: '',
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }], {
    hostClass: [{
      type: HostBinding,
      args: ['class.k-icon']
    }, {
      type: HostBinding,
      args: ['class.k-font-icon']
    }],
    hostAriaHidden: [{
      type: HostBinding,
      args: ['attr.aria-hidden']
    }],
    name: [{
      type: Input
    }]
  });
})();
const areSame = (i1, i2) => i1?.name === i2?.name && i1?.content === i2?.content && i1?.viewBox === i2?.viewBox && i1?.variants === i2?.variants;
/**
 * Represents the [Kendo UI SVG Icon component for Angular](https://www.telerik.com/kendo-angular-ui/components/icons/svgicon).
 * Used to display icons from the [Kendo UI SVG Icons](https://www.telerik.com/kendo-angular-ui/components/icons/svgicon/svgicon-list) collection.
 *
 * @example
 * ```ts
 * import { KENDO_ICONS } from "@progress/kendo-angular-icons";
 * import { paperclipIcon } from "@progress/kendo-svg-icons";
 *
 * @Component({
 *  selector: "my-app",
 *  imports: [KENDO_ICONS],
 *  template: `
 *   <kendo-svg-icon [icon]="paperclipIcon"></kendo-svg-icon>
 *  `,
 *  providers: [],
 * })
 * export class AppComponent {
 *  public paperclipIcon = paperclipIcon;
 * }
 * ```
 */
class SVGIconComponent extends IconBaseDirective {
  domSanitizer;
  element;
  renderer;
  hostClass = true;
  hostAriaHidden = true;
  /**
   * Sets the `SVGIcon` to render. Supports all [Kendo UI SVG Icons](https://www.telerik.com/kendo-angular-ui/components/icons/svgicon/svgicon-list).
   */
  set icon(icon) {
    const element = this.element.nativeElement;
    const hasDocument = isDocumentAvailable();
    const newName = icon?.name && icon.name !== this._icon?.name;
    if (this._icon && this._icon.name && newName && hasDocument) {
      this.renderer.removeClass(element, `k-svg-i-${this._icon.name}`);
    }
    if (!areSame(icon, this._icon)) {
      this._icon = icon;
    }
    if (hasDocument && newName) {
      this.renderer.addClass(element, `k-svg-i-${this._icon.name}`);
    }
  }
  get icon() {
    return this._icon;
  }
  /**
   * Sets the style variant of the icon.
   *
   * > Requires `@progress/kendo-svg-icons` v5 or later for the `variants` property to be available on icons.
   */
  set variant(variant) {
    this._variant = variant;
  }
  get variant() {
    return this._variant;
  }
  get content() {
    const variantContent = this.variant && this.icon?.variants?.[this.variant];
    const svgContent = variantContent || this.icon.content;
    return this.domSanitizer.bypassSecurityTrustHtml(svgContent);
  }
  get visible() {
    return this.icon && isDocumentAvailable();
  }
  _icon;
  _variant;
  constructor(domSanitizer, element, renderer) {
    super(element, renderer);
    this.domSanitizer = domSanitizer;
    this.element = element;
    this.renderer = renderer;
  }
  ngOnInit() {
    this.verifyIconProperty();
  }
  verifyIconProperty() {
    if (!isDevMode()) {
      return;
    }
    if (!this._icon) {
      throw new Error(`
                Invalid configuration.
                The input [icon] is required for the Kendo UI SVG Icon component for Angular.
                See https://www.telerik.com/kendo-angular-ui/components/icons/svg-icon
            `);
    }
  }
  static ɵfac = function SVGIconComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SVGIconComponent)(i0.ɵɵdirectiveInject(i1_1.DomSanitizer), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: SVGIconComponent,
    selectors: [["kendo-svg-icon"], ["kendo-svgicon"]],
    hostVars: 5,
    hostBindings: function SVGIconComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("aria-hidden", ctx.hostAriaHidden);
        i0.ɵɵclassProp("k-svg-icon", ctx.hostClass)("k-icon", ctx.hostClass);
      }
    },
    inputs: {
      icon: "icon",
      variant: "variant"
    },
    exportAs: ["kendoSVGIcon"],
    features: [i0.ɵɵInheritDefinitionFeature],
    decls: 1,
    vars: 1,
    consts: [["xmlns", "http://www.w3.org/2000/svg", 0, "xmlns", "xlink", "http://www.w3.org/1999/xlink", "aria-hidden", "true", 3, "pointerEvents", "innerHTML"], ["xmlns", "http://www.w3.org/2000/svg", 0, "xmlns", "xlink", "http://www.w3.org/1999/xlink", "aria-hidden", "true", 3, "innerHTML"]],
    template: function SVGIconComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵconditionalCreate(0, SVGIconComponent_Conditional_0_Template, 1, 4, ":svg:svg", 0);
      }
      if (rf & 2) {
        i0.ɵɵconditional(ctx.visible ? 0 : -1);
      }
    },
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SVGIconComponent, [{
    type: Component,
    args: [{
      exportAs: 'kendoSVGIcon',
      selector: 'kendo-svg-icon, kendo-svgicon',
      template: `
        @if (visible) {
          <svg [style.pointerEvents]="'none'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
            [attr.viewBox]="icon.viewBox" [innerHTML]="content" aria-hidden="true">
          </svg>
        }`,
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: true,
      imports: []
    }]
  }], () => [{
    type: i1_1.DomSanitizer
  }, {
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }], {
    hostClass: [{
      type: HostBinding,
      args: ['class.k-svg-icon']
    }, {
      type: HostBinding,
      args: ['class.k-icon']
    }],
    hostAriaHidden: [{
      type: HostBinding,
      args: ['attr.aria-hidden']
    }],
    icon: [{
      type: Input
    }],
    variant: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
const isPresent = value => value !== null && value !== undefined;

/**
 * Represents the service that manages icon settings.
 * Use the `notify` method to update icon settings dynamically.
 *
 * @example
 * ```typescript
 * constructor(private iconSettingsService: IconSettingsService) {}
 *
 * this.iconSettingsService.notify({ themeColor: 'primary' });
 * ```
 */
class IconSettingsService {
  /**
   * @hidden
   */
  changes = new Subject();
  /**
   * Notifies subscribers about changes in the icon settings.
   *
   * @param iconSettings - (Optional) Sets a new value for the [icon settings token](https://www.telerik.com/kendo-angular-ui/components/icons/api/icon_settings).
   * @example
   * ```typescript
   * iconSettingsService.notify({ size: 'large' });
   * ```
   */
  notify(iconSettings) {
    this.changes.next(iconSettings);
  }
  /**
   * Returns the [`SVGIcon`](https://www.telerik.com/kendo-angular-ui/components/icons/api/svgicon) object for the provided key.
   * Override in a custom service to provide custom SVG icons.
   *
   * @hidden
   */
  getSvgIcon(_name) {
    return null;
  }
  /**
   * Returns the list of classes to be rendered on the host `SPAN` element of custom font icons.
   * Override in a custom service to provide classes for custom font icons.
   *
   * @hidden
   */
  getCustomFontIconClass(_key) {
    return null;
  }
  static ɵfac = function IconSettingsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconSettingsService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: IconSettingsService,
    factory: IconSettingsService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconSettingsService, [{
    type: Injectable
  }], null, null);
})();

/**
 * Represents the token for the `IconSettings` of Kendo UI for Angular components.
 * Use this token to configure icon settings. ([See example.](https://www.telerik.com/kendo-angular-ui/components/icons/icon-settings#icons-configuration))
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 *
 * @NgModule({
 *   providers: [{ provide: ICON_SETTINGS, useValue: { type: 'svg', size: 'xsmall' } }]
 * })
 * export class AppModule {}
 * ```
 */
const ICON_SETTINGS = new InjectionToken('Kendo UI Icon-Settings token');
const DEFAULT_ICON_SETTINGS = {
  type: 'svg'
};
/**
 * Represents the service that manages icon settings.
 * Add this service to the `providers` array when you use it in a standalone component.
 *
 * @example
 * ```typescript
 * import { IconsService } from '@progress/kendo-angular-icons';
 *
 * @Component({
 *   selector: 'my-component',
 *   standalone: true,
 *   imports: [ ... ],
 *   providers: [IconsService, { provide: ICON_SETTINGS, useValue: { type: 'font' } }],
 *   template: `...`
 * })
 * export class AppComponent {}
 * ```
 */
class IconsService {
  _iconSettings;
  iconSettingsService;
  /**
   * Notifies subscribers of the initial icon settings and on each call to `notify`.
   * @hidden
   */
  changes = new BehaviorSubject(this.iconSettings || DEFAULT_ICON_SETTINGS);
  subs = new Subscription();
  constructor(_iconSettings, iconSettingsService) {
    this._iconSettings = _iconSettings;
    this.iconSettingsService = iconSettingsService;
    if (iconSettingsService) {
      this.subs.add(iconSettingsService.changes.pipe(map(iconSettings => isPresent(iconSettings) ? iconSettings : this._iconSettings), tap(iconSettings => this._iconSettings = iconSettings)).subscribe(iconSettings => this.changes.next(iconSettings)));
    }
    if (isPresent(this.iconSettings) && !areObjectsEqual(this.iconSettings, DEFAULT_ICON_SETTINGS)) {
      this.changes.next(this.iconSettings);
    }
  }
  /**
   * @hidden
   */
  get iconSettings() {
    return this._iconSettings;
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
  /**
   * Returns the [`SVGIcon`](https://www.telerik.com/kendo-angular-ui/components/icons/api/svgicon) object for the provided key.
   * Override in a custom service to provide custom SVG icons.
   * @hidden
   */
  getSvgIcon(name) {
    const customIcon = this.iconSettingsService && this.iconSettingsService.getSvgIcon(name);
    return customIcon;
  }
  /**
   * Returns the list of classes to be rendered on the host `SPAN` element of custom font icons.
   * Override in a custom service to provide classes for custom font icons.
   * @hidden
   */
  getCustomFontIconClass(key) {
    const customClass = this.iconSettingsService && this.iconSettingsService.getCustomFontIconClass(key);
    return customClass;
  }
  static ɵfac = function IconsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconsService)(i0.ɵɵinject(ICON_SETTINGS, 8), i0.ɵɵinject(IconSettingsService, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: IconsService,
    factory: IconsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [ICON_SETTINGS]
    }]
  }, {
    type: IconSettingsService,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * @hidden
 */
class IconWrapperComponent {
  iconsService;
  element;
  hostClass = true;
  /**
   * Sets the name for an existing font icon in a Kendo UI theme to render.
   * Supports all [Kendo UI Icons](https://www.telerik.com/kendo-angular-ui/components/styling/icons#icons-list).
   */
  name;
  /**
   * Sets the [SVGIcon](https://www.telerik.com/kendo-angular-ui/components/icons/api/svgicon) to render.
   */
  svgIcon;
  /**
   * Sets an additional class on the internal Icon component.
   */
  innerCssClass = '';
  /**
   * Sets a custom font icon class using the API of the consuming component.
   */
  customFontClass = '';
  /**
   * Sets the `IconSize` for the icon.
   */
  size;
  get customClasses() {
    const classes = [this.customFontClass, this.innerCssClass, this.customFontIconClass].filter(cl => !!cl).join(' ');
    return classes;
  }
  iconSettings;
  get hasSvgIcon() {
    this.svgIcon = this.iconsService.getSvgIcon(this.name) || this.svgIcon;
    if (this.svgIcon) {
      return true;
    }
    if (isDevMode()) {
      console.groupCollapsed(`Kendo UI for Angular: Icon type is set to 'svg', but only font icon name is provided. The "${this.name}" font icon will be rendered instead.`);
      console.warn(`
Starting from v7.0.0 (October, 2023) of the Kendo Themes, the font icons will be removed from the theme.
* To render an SVG icon, update the component configuration by setting the "svgIcon" input to the respective icon definition.
* To continue to use font icons, install the "@progress/kendo-font-icons" package and import "@progress/kendo-font-icons/dist/index.css".

Please note, that font icons are not compatible with a strict Content Security Policy and require the use of the "unsafe-inline" override.

For further details, check the following documentation articles:
https://www.telerik.com/kendo-angular-ui/components/icons/icon-settings
https://www.telerik.com/kendo-angular-ui/components/styling/icons/
https://www.telerik.com/kendo-angular-ui/components/styling/svg-icons/
https://www.telerik.com/design-system/docs/foundation/iconography/font-icons/
https://www.telerik.com/design-system/docs/foundation/iconography/icon-list/
            `);
      console.groupEnd();
    }
    return false;
  }
  get customFontIconClass() {
    return this.iconsService.getCustomFontIconClass(this.name) || this.customFontClass;
  }
  subs = new Subscription();
  constructor(iconsService, element) {
    this.iconsService = iconsService;
    this.element = element;
    this.subs = iconsService.changes.subscribe(iconSettings => {
      this.iconSettings = iconSettings;
    });
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
  static ɵfac = function IconWrapperComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconWrapperComponent)(i0.ɵɵdirectiveInject(IconsService), i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: IconWrapperComponent,
    selectors: [["kendo-icon-wrapper"]],
    hostVars: 2,
    hostBindings: function IconWrapperComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-icon-wrapper-host", ctx.hostClass);
      }
    },
    inputs: {
      name: "name",
      svgIcon: "svgIcon",
      innerCssClass: "innerCssClass",
      customFontClass: "customFontClass",
      size: "size"
    },
    exportAs: ["kendoIconWrapper"],
    decls: 2,
    vars: 1,
    consts: [[3, "ngClass", "icon", "size", "themeColor", "flip"], [3, "ngClass", "name", "size", "themeColor", "flip"], [3, "ngClass"]],
    template: function IconWrapperComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵconditionalCreate(0, IconWrapperComponent_Conditional_0_Template, 1, 5, "kendo-svgicon", 0)(1, IconWrapperComponent_Conditional_1_Template, 2, 1);
      }
      if (rf & 2) {
        i0.ɵɵconditional((ctx.iconSettings == null ? null : ctx.iconSettings.type) === "svg" && ctx.hasSvgIcon ? 0 : 1);
      }
    },
    dependencies: [NgClass, IconComponent, SVGIconComponent],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconWrapperComponent, [{
    type: Component,
    args: [{
      exportAs: 'kendoIconWrapper',
      selector: 'kendo-icon-wrapper',
      template: `
        @if (iconSettings?.type === 'svg' && hasSvgIcon) {
          <kendo-svgicon
            [ngClass]="innerCssClass"
            [icon]="svgIcon"
            [size]="size || iconSettings?.size"
            [themeColor]="iconSettings?.themeColor"
          [flip]="iconSettings?.flip"></kendo-svgicon>
        } @else {
          @if (!customFontIconClass) {
            <kendo-icon
              [ngClass]="innerCssClass"
              [name]="name"
              [size]="size || iconSettings?.size"
              [themeColor]="iconSettings?.themeColor"
            [flip]="iconSettings?.flip"></kendo-icon>
          } @else {
            <span [ngClass]="customClasses"></span>
          }
        }
        `,
      standalone: true,
      imports: [NgClass, IconComponent, SVGIconComponent]
    }]
  }], () => [{
    type: IconsService
  }, {
    type: i0.ElementRef
  }], {
    hostClass: [{
      type: HostBinding,
      args: ['class.k-icon-wrapper-host']
    }],
    name: [{
      type: Input
    }],
    svgIcon: [{
      type: Input
    }],
    innerCssClass: [{
      type: Input
    }],
    customFontClass: [{
      type: Input
    }],
    size: [{
      type: Input
    }]
  });
})();

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [`NgModule`](link:site.data.urls.angular['ngmoduleapi']) definition for the Icon component.
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 * import { IconModule } from '@progress/kendo-angular-icons';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *   declarations: [AppComponent],
 *   imports: [BrowserModule, IconModule],
 *   bootstrap: [AppComponent]
 * })
 * export class AppModule {}
 * ```
 */
class IconModule {
  static ɵfac = function IconModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: IconModule,
    imports: [IconComponent],
    exports: [IconComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconModule, [{
    type: NgModule,
    args: [{
      exports: [IconComponent],
      imports: [IconComponent]
    }]
  }], null, null);
})();

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [`NgModule`](link:site.data.urls.angular['ngmoduleapi']) definition for the SVG Icon component.
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 * import { SVGIconModule } from '@progress/kendo-angular-icons';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *   declarations: [AppComponent],
 *   imports: [BrowserModule, SVGIconModule],
 *   bootstrap: [AppComponent]
 * })
 * export class AppModule {}
 * ```
 */
class SVGIconModule {
  static ɵfac = function SVGIconModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SVGIconModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: SVGIconModule,
    imports: [SVGIconComponent],
    exports: [SVGIconComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SVGIconModule, [{
    type: NgModule,
    args: [{
      exports: [SVGIconComponent],
      imports: [SVGIconComponent]
    }]
  }], null, null);
})();

/**
 * Represents the utility array that contains all `KendoIcon`-related components and directives.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_ICON } from '@progress/kendo-angular-icons';
 *
 * @Component({
 *   selector: 'my-app',
 *   standalone: true,
 *   imports: [KENDO_ICON],
 *   template: `...`,
 * })
 * export class AppComponent {}
 * ```
 */
const KENDO_ICON = [IconComponent];
/**
 * Represents the utility array that contains all `KendoSVGIcon`-related components and directives.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
 *
 * @Component({
 *   selector: 'my-app',
 *   standalone: true,
 *   imports: [KENDO_SVGICON],
 *   template: `...`,
 * })
 * export class AppComponent {}
 * ```
 */
const KENDO_SVGICON = [SVGIconComponent];
/**
 * Represents the utility array that contains all `@progress/kendo-angular-icons`-related components and directives.
 *
 * @example
 * ```typescript
 * import { Component, ViewEncapsulation } from '@angular/core';
 * import { KENDO_ICONS } from '@progress/kendo-angular-icons';
 *
 * @Component({
 *   selector: 'my-app',
 *   standalone: true,
 *   imports: [KENDO_ICONS],
 *   template: `...`,
 * })
 * export class AppComponent {}
 * ```
 */
const KENDO_ICONS = [...KENDO_ICON, ...KENDO_SVGICON];

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [`NgModule`](link:site.data.urls.angular['ngmoduleapi']) definition for the Icons components.
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 * import { IconsModule } from '@progress/kendo-angular-icons';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *   declarations: [AppComponent],
 *   imports: [BrowserModule, IconsModule],
 *   bootstrap: [AppComponent]
 * })
 * export class AppModule {}
 * ```
 */
class IconsModule {
  static ɵfac = function IconsModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IconsModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: IconsModule,
    imports: [IconComponent, SVGIconComponent],
    exports: [IconComponent, SVGIconComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [IconsService]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IconsModule, [{
    type: NgModule,
    args: [{
      imports: [...KENDO_ICONS],
      exports: [...KENDO_ICONS],
      providers: [IconsService]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { ICON_SETTINGS, IconComponent, IconModule, IconSettingsService, IconWrapperComponent, IconsModule, IconsService, KENDO_ICON, KENDO_ICONS, KENDO_SVGICON, SVGIconComponent, SVGIconModule };